import * as XLSX from "xlsx";
import { ValidationResult, ProductCustomerRow, ValidationError } from "./types";
import axios from "axios";
import { getStoredAuthToken } from "../../../../utils/authToken";

const validateFloat = (value: string): number => {
    if (!value) return 0.0;
    const cleaned = value.replace(/[^\d.]/g, "");
    return cleaned ? parseFloat(cleaned) : 0.0;
};

// Cache for API lookups
const productCodeCache = new Set<string>();
const productNameCache = new Set<string>();
const customerCodeCache = new Set<string>();
const customerNameCache = new Set<string>();
const addonNameCache = new Set<string>();

const checkProductExists = async (productCode: string): Promise<boolean> => {
    if (productCodeCache.has(productCode)) return true;
    
    try {
        const token = getStoredAuthToken();
        const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
        const response = await axios.get(
            `${baseURL}/products/?product_code=${encodeURIComponent(productCode)}`,
            {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            }
        );
        
        const exists = response.data?.results?.length > 0 || response.data?.length > 0;
        if (exists) {
            productCodeCache.add(productCode);
        }
        return exists;
    } catch (error) {
        console.error(`Error checking product ${productCode}:`, error);
        return false;
    }
};

const checkCustomerExists = async (customerCode: string, customerName?: string): Promise<boolean> => {
    if (customerCode && customerCodeCache.has(customerCode)) return true;
    if (customerName && customerNameCache.has(customerName)) return true;
    
    try {
        const token = getStoredAuthToken();
        const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
        
        // Try by code first - use search parameter since customer_code is in search_fields
        if (customerCode) {
            try {
                const response = await axios.get(
                    `${baseURL}/customers/?search=${encodeURIComponent(customerCode)}`,
                    {
                        headers: {
                            Authorization: token ? `Bearer ${token}` : undefined,
                        },
                    }
                );
                
                // Check if any result matches the exact customer_code
                const results = response.data?.results || response.data || [];
                const exists = results.some((customer: any) => customer.customer_code === customerCode);
                if (exists) {
                    customerCodeCache.add(customerCode);
                    return true;
                }
            } catch (err: any) {
                console.error(`Error checking customer by code '${customerCode}':`, err);
            }
        }
        
        // Fallback to name - use search parameter
        if (customerName) {
            try {
                const response = await axios.get(
                    `${baseURL}/customers/?search=${encodeURIComponent(customerName)}`,
                    {
                        headers: {
                            Authorization: token ? `Bearer ${token}` : undefined,
                        },
                    }
                );
                
                // Check if any result matches the exact name
                const results = response.data?.results || response.data || [];
                const exists = results.some((customer: any) => customer.name === customerName);
                if (exists) {
                    customerNameCache.add(customerName);
                    return true;
                }
            } catch (err: any) {
                console.error(`Error checking customer by name '${customerName}':`, err);
            }
        }
        
        return false;
    } catch (error) {
        console.error(`Error checking customer ${customerCode}/${customerName}:`, error);
        return false;
    }
};

const checkAddonExists = async (addonName: string): Promise<boolean> => {
    if (addonNameCache.has(addonName)) return true;
    
    try {
        const token = getStoredAuthToken();
        const baseURL = process.env.REACT_APP_API_URL || "http://localhost:8000/api";
        const response = await axios.get(
            `${baseURL}/products/addons/?name=${encodeURIComponent(addonName)}`,
            {
                headers: {
                    Authorization: token ? `Bearer ${token}` : undefined,
                },
            }
        );
        
        const exists = response.data?.results?.length > 0 || response.data?.length > 0;
        if (exists) {
            addonNameCache.add(addonName);
        }
        return exists;
    } catch (error) {
        console.error(`Error checking addon ${addonName}:`, error);
        return false;
    }
};

export const validateExcelFile = async (file: File): Promise<ValidationResult> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = async (e) => {
            try {
                const data = new Uint8Array(e.target?.result as ArrayBuffer);
                const workbook = XLSX.read(data, { type: "array" });
                
                let sheetName: string;
                if (workbook.SheetNames && workbook.SheetNames.length > 0) {
                    sheetName = workbook.SheetNames[0];
                } else {
                    const sheetKeys = Object.keys(workbook.Sheets);
                    sheetName = sheetKeys[0] || "";
                }
                
                if (!sheetName || !workbook.Sheets[sheetName]) {
                    reject(new Error(`Không tìm thấy sheet đầu tiên. Các sheet có sẵn: ${workbook.SheetNames.join(", ")}`));
                    return;
                }
                
                const worksheet = workbook.Sheets[sheetName];
                const jsonData: any[] = XLSX.utils.sheet_to_json(worksheet, { header: 1, defval: null });

                if (jsonData.length < 3) {
                    reject(new Error("File phải có ít nhất 3 dòng (headers ở dòng 3)"));
                    return;
                }

                // Get headers from row 3 (index 2) - database field names
                const headerRow = jsonData[2];
                if (!headerRow || headerRow.length === 0) {
                    reject(new Error("Không tìm thấy headers ở dòng 3"));
                    return;
                }

                // Map headers to field names
                const fieldMap: { [key: string]: number } = {};
                headerRow.forEach((header: any, colIdx: number) => {
                    const headerStr = header ? String(header).trim().toLowerCase() : "";
                    if (headerStr) {
                        fieldMap[headerStr] = colIdx;
                    }
                });

                // Validate required headers (addons are optional)
                const requiredFields = ['product_code', 'product_name', 'base_product_code', 'customer_code'];
                const missingFields = requiredFields.filter(field => !(field in fieldMap));
                if (missingFields.length > 0) {
                    reject(new Error(`Thiếu các trường bắt buộc: ${missingFields.join(", ")}`));
                    return;
                }

                // Parse data rows starting from row 4 (index 3)
                const rows: ProductCustomerRow[] = [];
                const errors: ValidationError[] = [];
                const productCodesInBatch = new Map<string, number[]>();
                const productNamesInBatch = new Map<string, number[]>();

                for (let rowIdx = 3; rowIdx < jsonData.length; rowIdx++) {
                    const row = jsonData[rowIdx];
                    
                    // Skip empty rows
                    if (!row || row.every((cell: any) => cell === null || cell === undefined || String(cell).trim() === "")) {
                        continue;
                    }

                    const rowData: any = {};
                    const rowErrors: ValidationError[] = [];
                    let isValid = true;

                    // Extract field values
                    const productCode = row[fieldMap['product_code']] ? String(row[fieldMap['product_code']]).trim() : "";
                    const productName = row[fieldMap['product_name']] ? String(row[fieldMap['product_name']]).trim() : "";
                    const baseProductCode = row[fieldMap['base_product_code']] ? String(row[fieldMap['base_product_code']]).trim() : "";
                    const customerCode = row[fieldMap['customer_code']] ? String(row[fieldMap['customer_code']]).trim() : "";
                    const customerName = row[fieldMap['customer_name']] ? String(row[fieldMap['customer_name']]).trim() : "";
                    const addonName = row[fieldMap['addon_name']] ? String(row[fieldMap['addon_name']]).trim() : "";
                    const addonQuantity = row[fieldMap['addon_quantity']] ? String(row[fieldMap['addon_quantity']]).trim() : "";
                    const price = row[fieldMap['price']] ? String(row[fieldMap['price']]).trim() : "";
                    const note = row[fieldMap['note']] ? String(row[fieldMap['note']]).trim() : "";
                    const quantity = row[fieldMap['quantity']] ? String(row[fieldMap['quantity']]).trim() : "";

                    // Build row data
                    rowData.product_code = productCode;
                    rowData.product_name = productName;
                    rowData.base_product_code = baseProductCode;
                    rowData.customer_code = customerCode;
                    rowData.customer_name = customerName || undefined;
                    rowData.addon_name = addonName;
                    rowData.addon_quantity = addonQuantity;
                    rowData.price = price;
                    rowData.note = note;
                    rowData.quantity = quantity;

                    // Validate required fields
                    if (!productCode) {
                        rowErrors.push({ row: rowIdx + 1, field: 'product_code', error: 'Mã sản phẩm là bắt buộc' });
                        isValid = false;
                    }
                    if (!productName) {
                        rowErrors.push({ row: rowIdx + 1, field: 'product_name', error: 'Tên sản phẩm là bắt buộc' });
                        isValid = false;
                    }
                    if (!baseProductCode) {
                        rowErrors.push({ row: rowIdx + 1, field: 'base_product_code', error: 'Mã sản phẩm gốc là bắt buộc' });
                        isValid = false;
                    }
                    if (!customerCode) {
                        rowErrors.push({ row: rowIdx + 1, field: 'customer_code', error: 'Mã khách hàng là bắt buộc' });
                        isValid = false;
                    }

                    // Validate addon_quantity > 0 (only if provided)
                    if (addonQuantity) {
                        const quantity = validateFloat(addonQuantity);
                        if (quantity <= 0) {
                            rowErrors.push({ row: rowIdx + 1, field: 'addon_quantity', error: 'Số lượng dịch vụ kèm theo phải lớn hơn 0' });
                            isValid = false;
                        }
                    }
                    
                    // If addon_name is provided but addon_quantity is missing, warn
                    if (addonName && !addonQuantity) {
                        rowErrors.push({ 
                            row: rowIdx + 1, 
                            field: 'addon_quantity', 
                            error: 'Số lượng dịch vụ kèm theo là bắt buộc nếu có tên dịch vụ kèm theo',
                            isWarning: false
                        });
                        isValid = false;
                    }
                    
                    // If addon_quantity is provided but addon_name is missing, warn
                    if (addonQuantity && !addonName) {
                        rowErrors.push({ 
                            row: rowIdx + 1, 
                            field: 'addon_name', 
                            error: 'Tên dịch vụ kèm theo là bắt buộc nếu có số lượng dịch vụ kèm theo',
                            isWarning: false
                        });
                        isValid = false;
                    }

                    // Track product codes and names for uniqueness check
                    if (productCode) {
                        if (!productCodesInBatch.has(productCode)) {
                            productCodesInBatch.set(productCode, []);
                        }
                        productCodesInBatch.get(productCode)!.push(rowIdx + 1);
                    }
                    if (productName) {
                        if (!productNamesInBatch.has(productName)) {
                            productNamesInBatch.set(productName, []);
                        }
                        productNamesInBatch.get(productName)!.push(rowIdx + 1);
                    }

                    // Check base product exists (required field - always check if provided)
                    if (baseProductCode) {
                        const exists = await checkProductExists(baseProductCode);
                        if (!exists) {
                            rowErrors.push({ 
                                row: rowIdx + 1, 
                                field: 'base_product_code', 
                                error: `Sản phẩm gốc '${baseProductCode}' không tồn tại trong hệ thống` 
                            });
                            isValid = false;
                        }
                    }

                    // Check customer exists (required field - always check if customer_code is provided)
                    // Note: customerCode is required, so this check should always run
                    if (customerCode) {
                        const exists = await checkCustomerExists(customerCode, customerName);
                        if (!exists) {
                            rowErrors.push({ 
                                row: rowIdx + 1, 
                                field: 'customer_code', 
                                error: `Khách hàng '${customerCode}' không tồn tại trong hệ thống` 
                            });
                            isValid = false;
                        }
                    } else if (customerName) {
                        // Fallback: If customer_code is missing but customer_name is provided, check by name
                        const exists = await checkCustomerExists('', customerName);
                        if (!exists) {
                            rowErrors.push({ 
                                row: rowIdx + 1, 
                                field: 'customer_code', 
                                error: `Khách hàng '${customerName}' không tồn tại trong hệ thống` 
                            });
                            isValid = false;
                        }
                    }

                    // Check addon exists
                    if (addonName) {
                        const exists = await checkAddonExists(addonName);
                        if (!exists) {
                            rowErrors.push({ 
                                row: rowIdx + 1, 
                                field: 'addon_name', 
                                error: `Dịch vụ kèm theo '${addonName}' không tồn tại trong hệ thống` 
                            });
                            isValid = false;
                        }
                    }

                    rows.push({
                        rowNumber: rowIdx + 1,
                        data: rowData,
                        isValid,
                        errors: rowErrors,
                    });

                    errors.push(...rowErrors);
                }

                // Check for duplicate product codes (warnings)
                productCodesInBatch.forEach((rowNumbers, productCode) => {
                    if (rowNumbers.length > 1) {
                        errors.push({
                            row: rowNumbers[0],
                            field: 'product_code',
                            error: `Mã sản phẩm '${productCode}' xuất hiện nhiều lần trong file (dòng ${rowNumbers.join(", ")})`,
                            isWarning: true,
                        });
                    }
                });

                // Check for duplicate product names (warnings)
                productNamesInBatch.forEach((rowNumbers, productName) => {
                    if (rowNumbers.length > 1) {
                        errors.push({
                            row: rowNumbers[0],
                            field: 'product_name',
                            error: `Tên sản phẩm '${productName}' xuất hiện nhiều lần trong file (dòng ${rowNumbers.join(", ")})`,
                            isWarning: true,
                        });
                    }
                });

                resolve({
                    rows,
                    errors,
                    originalRowCount: rows.length,
                });
            } catch (error: any) {
                reject(new Error(`Lỗi khi xử lý file: ${error.message || error.toString()}`));
            }
        };
        reader.onerror = () => {
            reject(new Error("Lỗi khi đọc file"));
        };
        reader.readAsArrayBuffer(file);
    });
};

